import { Menu, Search } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import { DgbSymbol } from '@/components/brand-logo';
import { Button } from '@/components/ui/button';
import { ThemeToggle } from '@/theme/ThemeToggle';
import { DensityToggle } from '../density/DensityToggle';
import { StagingChip } from '../StagingBar';
import { LanguageSwitcher } from '../LanguageSwitcher';
import type { ActiveLocation } from '../model/active-location';
import type { ResolvedModule } from '../model/resolve-navigation';
import { AttentionBell } from './AttentionBell';
import { ShellBreadcrumb } from './ShellBreadcrumb';

/**
 * Decision 5: one row, always.
 *
 * WHAT LEFT, AND WHY THAT IS THE FIX. The old header carried the context selector, an
 * `Amministrazione` link, the identity email as a profile link, and `Esci` — plus theme and
 * language — and was `flex-wrap` so the group could drop onto a second row when it did not fit.
 * Its own code comment recorded that wrap as intended. But wrapping does not solve crowding, it
 * hides it: at 1040–1280px the header was two rows tall on every screen in the platform. The four
 * that left went somewhere better rather than nowhere: administration is a rail module, profile
 * and sign-out are the rail's account disclosure, and the scope selector is the chip row directly
 * beneath this one, where it is now visible at EVERY width instead of desktop only.
 *
 * WHAT STAYS is a breadcrumb that names the screen, a search trigger, the notifications bell
 * (decision 54) and the three display preferences. Three grid tracks, none of which may wrap.
 *
 * The burger is inside the breadcrumb track and shown by CSS below 900px, where the rail leaves
 * the flow. It is never in the same track as the preferences, so it cannot be pushed off-row by
 * them — the specific failure the old header's wrap was working around.
 *
 * ON A PHONE THIS ROW IS THE TOP BAR (Brand Kit § The frame): the symbol, as the link home, and the
 * screen title at 18/700. The burger gives way to the symbol there because the bottom bar's fifth
 * entry, "Altro", opens the same drawer; theme and language move into that drawer, because the
 * row has room for the title, the search and the bell and nothing more.
 */
export function ShellHeader({
  active,
  modules,
  onOpenDrawer,
  onOpenCommandPalette,
  drawerOpen,
}: {
  active: ActiveLocation;
  modules: readonly ResolvedModule[];
  onOpenDrawer: () => void;
  onOpenCommandPalette: () => void;
  drawerOpen: boolean;
}) {
  const { t } = useTranslation('shell');

  return (
    <header className="shell-header">
      <div className="shell-header-left">
        {/* Wrapped for the same reason as the density control below: `Button`'s own cva base
            carries `inline-flex`, a utility, which beat `shell.css`'s `display: none` and left the
            burger visible at every width — including 1400px, where the rail is right there. The
            wrapper gives this file an element with no utilities on it, and the shared primitive
            keeps its focus ring and its 44px touch target. */}
        <span className="shell-burger">
          <Button
            type="button"
            variant="ghost"
            size="icon"
            aria-expanded={drawerOpen}
            aria-controls="app-nav-drawer"
            aria-label={t('nav.openMenu')}
            onClick={onOpenDrawer}
          >
            <Menu aria-hidden="true" />
          </Button>
        </span>
        {/* Shown by CSS on a phone only, where it takes the burger's place. */}
        <Link to="/" className="shell-home-mark" aria-label={t('nav.home')}>
          <DgbSymbol alt="" />
        </Link>
        <ShellBreadcrumb active={active} />
        {/* Staging's mark in the phone top bar (#353), in the bar itself rather than floating
            over the page, as Stripe marks test mode in its header. `shell.css` shows it on a
            phone only; wider, `StagingBar`'s tab has room in the frame margin. */}
        <StagingChip />
      </div>

      {/* A trigger, not a field. A real input here would have to decide what it searches before
          the cross-module search read exists; a trigger that opens the palette promises exactly
          what the palette delivers. The shortcut is shown on the control that uses it, which is
          how a keyboard user learns it exists. */}
      <button
        type="button"
        onClick={onOpenCommandPalette}
        aria-label={t('command.open')}
        className="shell-search"
      >
        <Search aria-hidden="true" className="size-4 shrink-0" />
        <span className="shell-search-label">{t('command.placeholder')}</span>
        <kbd className="shell-kbd">{t('command.shortcut')}</kbd>
      </button>

      <div className="shell-header-right">
        <AttentionBell modules={modules} />
        {/* A wrapper with NO Tailwind display utility on it, deliberately: `shell.css` owns this
            element's display, and a utility would beat it — the `utilities` layer wins over
            `components` unconditionally. It is the hook the < 620px block removes this control by;
            see there for why density is the one that goes on a phone. */}
        <span className="shell-density">
          <DensityToggle />
        </span>
        {/* The same kind of wrapper, for the < 620px block to move theme and language into the
            drawer (`DrawerPreferences.tsx`). */}
        <span className="shell-prefs">
          <ThemeToggle />
          <LanguageSwitcher />
        </span>
      </div>
    </header>
  );
}
